import React from 'react'
import { Tooltip } from '@mui/material'
import {
  ArrowBack, ArrowForward, DarkMode as DarkModeIcon, LightMode as LightModeIcon, Search as SearchIcon, EditOutlined,
  VerifiedUserOutlined, HourglassTopRounded, CheckRounded, BlockRounded,
} from '@mui/icons-material'
import { PrivatePage } from '../component/PagePrivate'
import { ViewToggle } from '../component/ViewToggle'
import { NotificationV2 } from '../container/common/NotificationV2'
import { ProfileV2 } from '../container/common/ProfileV2'
import { ConfirmV2 } from '../component/ConfirmV2'
import { api } from '../function/axios'
import { formatDate } from '../function/date'
import { useDispatch, useSelector } from '../store'
import { toggleAuthorize, toggleBlock } from '../store/user/actionCreator'
import { getDepartment } from '../store/auth/actionCreator'
import { Admin } from '../store/user/reducer'
import { Role } from '../enum/role'
import { useAdminTheme } from '../hook/useAdminTheme'
import { useAuthEffect } from '../hook/useAuthEffect'
import { EditAdminDialogV2 } from '../component/admins-v2/EditAdminDialogV2'
import l from './SubscribersPageV2.module.css'
import s from '../component/admins-v2/AdminsV2.module.css'

/*
 * Admins in the v2 admin design. The classic page (AdminsPage.tsx) is
 * untouched; this one reads the same list (/users/users), approves and
 * revokes through the same role change (PUT /auth/admin/role: ADMIN ⇄
 * CANDIDATE-ADMIN), blocks through the same call, and edits with the same
 * rules (EditAdminDialogV2 — HR and the super-admin only, as there).
 *
 * The backend's search matches whole words only, and the list is small, so
 * it is read in full and searched here (any part of a name, email, phone or
 * department).
 *
 * Differences on purpose: a super-admin's access is not a switch here — the
 * classic "Authorized" box is empty for them and a click would make them a
 * plain admin; your own access and block are locked, so no one locks
 * themselves out; revoking and blocking ask first. A blocked admin's role
 * cannot change (the backend refuses), so approving waits for an unblock.
 */

const PER_PAGE = 12
const CANDIDATE = 'CANDIDATE-ADMIN'

type Row = Admin & { is2FAEnabled?: boolean }
type StatusFilter = 'all' | 'admin' | 'pending' | 'blocked'
type Ask = { kind: 'revoke' | 'block' | 'unblock'; row: Row }

const nameOf = (r: Row) => [r.firstname, r.lastname].filter(Boolean).join(' ') || r.email || r.phone || `#${r.id}`
const initials = (r: Row) => (`${(r.firstname || r.email || '?')[0]}${(r.lastname || '')[0] || ''}`).toUpperCase()

function roleOf(r: Row): { label: string; tone: 'super' | 'admin' | 'pending' | 'other' } {
  const n = r.role?.name
  if (n === Role.SUPER_ADMIN) return { label: 'Super-admin', tone: 'super' }
  if (n === Role.ADMIN) return { label: 'Admin', tone: 'admin' }
  if (n === CANDIDATE) return { label: 'Waiting for approval', tone: 'pending' }
  return { label: n || '—', tone: 'other' }
}

function Avatar({ r }: { r: Row }) {
  const [broken, setBroken] = React.useState(false)
  return r.avatar && !broken
    ? <img className={s.avatar} src={r.avatar} alt="" onError={() => setBroken(true)} />
    : <span className={s.avatar}>{initials(r)}</span>
}

function Switch({ on, disabled, title, onChange, danger }: { on: boolean; disabled?: boolean; title: string; onChange: () => void; danger?: boolean }) {
  return (
    <Tooltip title={title}>
      <span>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={title}
          className={`${s.switch} ${on ? (danger ? s.switchDanger : s.switchOn) : ''}`}
          disabled={disabled}
          onClick={onChange}
        >
          <span />
        </button>
      </span>
    </Tooltip>
  )
}

export function AdminsPageV2() {
  const dispatch = useDispatch()
  const { theme, toggleTheme } = useAdminTheme()
  const me = useSelector((state) => state.settings)
  const { department: departments } = useSelector((state) => state.auth)

  const [rows, setRows] = React.useState<Row[] | null>(null)
  const [query, setQuery] = React.useState('')
  const [status, setStatus] = React.useState<StatusFilter>('all')
  const [dept, setDept] = React.useState<number | 'all'>('all')
  const [page, setPage] = React.useState(0)
  const [editing, setEditing] = React.useState<Row | null>(null)
  const [ask, setAsk] = React.useState<Ask | null>(null)

  useAuthEffect(() => { dispatch(getDepartment) })

  const load = React.useCallback(async () => {
    try {
      const { data } = await api({ method: 'GET', url: '/users/users', params: { offset: 0, limit: 1000 } })
      setRows(Array.isArray(data) ? data : data?.list || [])
    } catch {
      setRows([])
    }
  }, [])
  React.useEffect(() => { void load() }, [load])
  React.useEffect(() => { setPage(0) }, [query, status, dept])

  const myDept = (me.department as unknown as { name?: string } | null)?.name
  const canEdit = myDept === 'HR' || me.role === Role.SUPER_ADMIN
  const isMe = (r: Row) => r.id === me.id || (!!me.uid && r.uid === me.uid)

  const all = rows || []
  const isPending = (r: Row) => r.role?.name === CANDIDATE && !r.isBlocked
  const isAdmin = (r: Row) => (r.role?.name === Role.ADMIN || r.role?.name === Role.SUPER_ADMIN) && !r.isBlocked
  const counts = {
    admin: all.filter(isAdmin).length,
    pending: all.filter(isPending).length,
    blocked: all.filter((r) => r.isBlocked).length,
    twoFA: all.filter((r) => isAdmin(r) && r.is2FAEnabled).length,
  }
  const pending = all.filter(isPending)

  const q = query.trim().toLowerCase()
  const list = all
    .filter((r) => status === 'all' || (status === 'admin' ? isAdmin(r) : status === 'pending' ? isPending(r) : r.isBlocked))
    .filter((r) => dept === 'all' || r.departmentId === dept)
    .filter((r) => !q || [nameOf(r), r.email, r.phone, r.department?.name].some((v) => (v || '').toLowerCase().includes(q)))
  const pages = Math.max(1, Math.ceil(list.length / PER_PAGE))
  const shown = list.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE)

  const approve = (r: Row) => dispatch(toggleAuthorize(r.id, true, () => { void load() }))
  function confirm() {
    if (!ask) return
    const { kind, row } = ask
    if (kind === 'revoke') dispatch(toggleAuthorize(row.id, false, () => { void load() }))
    else dispatch(toggleBlock(row.id, kind === 'block', () => { void load() }))
  }

  const header = (
    <div className={`${l.theme} ${l.header}`} data-theme={theme}>
      <div className={l.crumb}><small>Team</small><b>Admins</b></div>
      <div className={l.spacer} />
      <label className={l.search}>
        <SearchIcon sx={{ fontSize: 17 }} />
        <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, email, phone, department" />
      </label>
      <ViewToggle dark={theme === 'dark'} />
      <Tooltip title={theme === 'dark' ? 'Light theme' : 'Dark theme'}>
        <button className={l.iconBtn} onClick={toggleTheme} aria-label="Toggle theme">
          {theme === 'dark' ? <LightModeIcon sx={{ fontSize: 17 }} /> : <DarkModeIcon sx={{ fontSize: 17 }} />}
        </button>
      </Tooltip>
      <NotificationV2 />
      <ProfileV2 />
    </div>
  )

  return (
    <PrivatePage header={header}>
      <div className={`${l.theme} ${l.page}`} data-theme={theme}>
        <div className={`${l.kpis} ${s.kpis}`}>
          <div className={l.kpi}><small>Team</small><b>{rows ? all.length : '—'}</b><span>with admin-panel accounts</span></div>
          <div className={l.kpi}><small>Admins</small><b style={{ color: 'var(--ok)' }}>{rows ? counts.admin : '—'}</b><span>have access now</span></div>
          <div className={l.kpi}><small>Waiting</small><b style={{ color: counts.pending ? 'var(--warn)' : undefined }}>{rows ? counts.pending : '—'}</b><span>for approval</span></div>
          <div className={l.kpi}><small>Blocked</small><b>{rows ? counts.blocked : '—'}</b><span>cannot sign in</span></div>
          <div className={l.kpi}><small>2FA</small><b>{rows ? `${counts.twoFA}/${counts.admin}` : '—'}</b><span>admins with two-factor on</span></div>
        </div>

        {pending.length > 0 && (
          <div className={s.pending}>
            <div className={s.pendingHead}><HourglassTopRounded sx={{ fontSize: 18 }} /><b>Waiting for approval</b><span>{pending.length}</span></div>
            <div className={s.pendingList}>
              {pending.map((r) => (
                <div key={r.id} className={s.pendingCard}>
                  <Avatar r={r} />
                  <div className={s.who}>
                    <b>{nameOf(r)}</b>
                    <span>{r.email || r.phone}{r.department?.name ? ` · ${r.department.name}` : ''}</span>
                    {r.createdTime && <span>signed up {formatDate(r.createdTime, 'dd.MM.yyyy')}</span>}
                  </div>
                  <button type="button" className={s.approve} onClick={() => approve(r)} disabled={isMe(r)}><CheckRounded sx={{ fontSize: 16 }} />Approve</button>
                  <Tooltip title="Block"><button type="button" className={`${s.act} ${s.actDanger}`} onClick={() => setAsk({ kind: 'block', row: r })} disabled={isMe(r)} aria-label="Block"><BlockRounded sx={{ fontSize: 16 }} /></button></Tooltip>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className={l.sec}>
          <div className={s.chips}>
            {([['all', 'All', all.length], ['admin', 'Admins', counts.admin], ['pending', 'Waiting', counts.pending], ['blocked', 'Blocked', counts.blocked]] as const).map(([k, label, n]) => (
              <button key={k} type="button" className={`${l.chip} ${status === k ? l.chipOn : ''}`} onClick={() => setStatus(k)}>
                {label}{rows ? <span className={s.chipCount}>{n}</span> : null}
              </button>
            ))}
          </div>
          <span className={s.sep} />
          <div className={s.chips}>
            <button type="button" className={`${l.chip} ${dept === 'all' ? l.chipOn : ''}`} onClick={() => setDept('all')}>All departments</button>
            {(departments || []).map((d) => (
              <button key={d.id} type="button" className={`${l.chip} ${dept === d.id ? l.chipOn : ''}`} onClick={() => setDept(d.id)}>{d.name}</button>
            ))}
          </div>
        </div>

        <div className={l.card}>
          <div className={l.scroll}>
            {rows === null ? (
              <div className={l.empty}>Loading…</div>
            ) : !shown.length ? (
              <div className={l.empty}>{q || status !== 'all' || dept !== 'all' ? 'Nothing matches the filter' : 'No admins yet'}</div>
            ) : (
              <table className={l.table}>
                <thead>
                  <tr>
                    <th>Admin</th>
                    <th>Phone</th>
                    <th>Department</th>
                    <th>Role</th>
                    <th>2FA</th>
                    <th>Added</th>
                    <th className={s.center}>Access</th>
                    <th className={s.center}>Blocked</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {shown.map((r) => {
                    const role = roleOf(r)
                    const self = isMe(r)
                    const authorized = r.role?.name === Role.ADMIN || r.role?.name === Role.SUPER_ADMIN
                    const accessTitle = self ? 'Your own access' : role.tone === 'super' ? 'Super-admin — access is not changed here'
                      : r.isBlocked ? 'Unblock first — a blocked admin’s role cannot change' : authorized ? 'Revoke admin access' : 'Approve as admin'
                    return (
                      <tr key={r.id} className={r.isBlocked ? s.rowBlocked : ''}>
                        <td>
                          <div className={s.person}>
                            <Avatar r={r} />
                            <div className={s.who}>
                              <b>{nameOf(r)}{self && <em className={s.you}>you</em>}</b>
                              <span>{r.email}</span>
                            </div>
                          </div>
                        </td>
                        <td className={l.nowrap}>{r.phone || <span className={l.muted}>—</span>}</td>
                        <td>{r.department?.name ? <span className={s.tag}>{r.department.name}</span> : <span className={l.muted}>—</span>}</td>
                        <td className={l.nowrap}>
                          <span className={`${s.role} ${s[`role_${role.tone}`]}`}>
                            {role.tone === 'super' && <VerifiedUserOutlined sx={{ fontSize: 14 }} />}{r.isBlocked ? 'Blocked' : role.label}
                          </span>
                        </td>
                        <td>
                          {r.is2FAEnabled
                            ? <span className={s.on2fa}>On</span>
                            : <span className={l.muted}>Off</span>}
                        </td>
                        <td className={l.nowrap}>{r.createdTime ? formatDate(r.createdTime, 'dd.MM.yyyy') : '—'}</td>
                        <td className={s.center}>
                          <Switch
                            on={authorized}
                            disabled={self || role.tone === 'super' || r.isBlocked}
                            title={accessTitle}
                            onChange={() => (authorized ? setAsk({ kind: 'revoke', row: r }) : approve(r))}
                          />
                        </td>
                        <td className={s.center}>
                          <Switch
                            on={r.isBlocked}
                            danger
                            disabled={self}
                            title={self ? 'You cannot block yourself' : r.isBlocked ? 'Unblock' : 'Block'}
                            onChange={() => setAsk({ kind: r.isBlocked ? 'unblock' : 'block', row: r })}
                          />
                        </td>
                        <td>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <button type="button" className={s.act} onClick={() => setEditing(r)} aria-label="Edit"><EditOutlined sx={{ fontSize: 16 }} /></button>
                            </Tooltip>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>
          {pages > 1 && (
            <div className={s.pager}>
              <span>{page * PER_PAGE + 1}–{Math.min(list.length, (page + 1) * PER_PAGE)} of {list.length}</span>
              <div className={s.pagerBtns}>
                <button type="button" className={s.act} disabled={page === 0} onClick={() => setPage(page - 1)} aria-label="Previous page"><ArrowBack sx={{ fontSize: 16 }} /></button>
                <button type="button" className={s.act} disabled={page >= pages - 1} onClick={() => setPage(page + 1)} aria-label="Next page"><ArrowForward sx={{ fontSize: 16 }} /></button>
              </div>
            </div>
          )}
        </div>
      </div>

      {editing && (
        <EditAdminDialogV2 admin={editing} departments={departments || []} theme={theme} onClose={() => setEditing(null)} onSaved={() => { void load() }} />
      )}
      {ask && (
        <ConfirmV2
          header={
            ask.kind === 'revoke' ? `Revoke admin access for ${nameOf(ask.row)}? They go back to “waiting for approval”.`
              : ask.kind === 'block' ? `Block ${nameOf(ask.row)}? They will not be able to sign in.`
                : `Unblock ${nameOf(ask.row)}?`
          }
          onYes={confirm}
          onClose={() => setAsk(null)}
        />
      )}
    </PrivatePage>
  )
}
